import { error } from 'react-toastify-redux';
import moment from 'moment';
import { parse } from 'json2csv';
import { deliverySlotsCollection, productsCollection } from '../../firebase';
import { decompressData } from '../../utils/compress';
import { startLoading, stopLoading } from './ui';

export const DeliveryOrdersActionTypes = {
  GET_DELIVERY_ORDERS: 'GET_DELIVERY_ORDERS',
};

const subVariantKey = (subVariants) =>
  (subVariants || [])
    .map((s) => `${s.key}:${s.assetId}`)
    .sort()
    .join('|');

/**
 * Delivery order lines carry no sku, so it is resolved from the catalogue the
 * same way the API matches an order line to a variant (utils/order.js:415-435):
 * variant type plus the subVariant assetIds. The line's `uuid` is not a
 * catalogue key and cannot be used for this.
 *
 * Only the products actually present in the export are fetched.
 */
const buildSkuLookup = async (items) => {
  const ids = new Set();
  items.forEach((item) =>
    (item.products || []).forEach((p) => p.id && ids.add(p.id))
  );

  const docs = await Promise.all(
    [...ids].map((id) =>
      productsCollection
        .doc(id)
        .get()
        .catch(() => null)
    )
  );

  const byProduct = new Map();
  docs.forEach((doc) => {
    if (!doc || !doc.exists) return;
    const data = decompressData(doc.data());
    byProduct.set(doc.id, data.variants || []);
  });

  return (product) => {
    const variants = byProduct.get(product.id);
    if (!variants) return '';
    const wanted = subVariantKey(product.subVariants);
    const match = variants.find(
      (v) =>
        v.type === product.variant &&
        (!wanted || subVariantKey(v.subVariants) === wanted)
    );
    return (match && match.sku) || '';
  };
};

/**
 * Retrieve multiple delivery orders from firebase
 */
export const getDeliveryOrder = ({ params }) => {
  return async (dispatch, getState) => {
    try {
      dispatch(startLoading());
      const {
        deliveryOrders: { deliveryOrders },
      } = getState();

      let { dateFrom, dateTo } = params;
      const { productId, variant } = params;
      let query = deliverySlotsCollection;
      if (dateFrom && dateTo) {
        dateFrom = moment(dateFrom).startOf('day').toDate();
        dateTo = moment(dateTo).endOf('day').toDate();
        query = query.where('date', '>=', dateFrom).where('date', '<=', dateTo);
      }

      const documentSnapshots = await query.get();
      const items = [];

      documentSnapshots.docs.forEach(async (doc) => {
        const data = doc.data();
        data.slots.forEach((s) => {
          if (s.orders && s.orders.length) {
            s.orders.forEach((o) => {
              if (productId && !o.products.find((p) => p.id === productId))
                return;
              if (variant && !o.products.find((p) => p.variant === variant))
                return;
              items.push({
                deliveryDate: data.date,
                deliveryTime: s.time,
                ...o,
              });
            });
          }
        });
      });

      const { size } = deliveryOrders.paging;
      const totalPages = parseInt(items.length / size) + 1;
      deliveryOrders.paging.totalPages = totalPages;

      dispatch({
        type: DeliveryOrdersActionTypes.GET_DELIVERY_ORDERS,
        deliveryOrders: {
          items,
          paging: deliveryOrders.paging,
        },
      });
    } catch (err) {
      dispatch(error(err.message));
    } finally {
      dispatch(stopLoading());
    }
  };
};

/**
 * Export multiple orders from firebase
 * @param {string} paging.orderBy order by field name
 * @param {string} paging.order get desc or asc
 * @param {string} filter filter text
 * @param {object} params collection fields
 */
export const exportCsv = (orders) => {
  return async (dispatch) => {
    try {
      dispatch(startLoading());
      const csvData = orders.map((data) => {
        let productList = '';
        let shippingDetails = '';

        shippingDetails = `${data.shipping.address}, ${data.shipping.country}`;

        data.products.forEach((product) => {
          productList += `Qty:${product.qty}  ${product.name}--${product.variant}\r\n`;
          if (product.subVariants && product.subVariants.length > 0) {
            product.subVariants
              .filter((sub) => sub.value)
              .forEach((sub) => {
                productList += `    +${sub.value}\r\n`;
              });
            productList += `\r\n`;
          }
        });

        return {
          csv_refNum: data.refNum,
          csv_customer: `${data.shipping.firstName} ${data.shipping.lastName}`,
          csv_productList: productList,
          csv_shippingDetails: shippingDetails,
          csv_contact: data.shipping.contactNo,
        };
      });

      const fields = Object.keys(csvData[0]);
      const opts = { fields };
      let csv = parse(csvData, opts);
      csv = csv
        .replace('csv_refNum', 'Ref No')
        .replace('csv_customer', 'Customer')
        .replace('csv_productList', 'Products')
        .replace('csv_shippingDetails', 'Shipping Details')
        .replace('csv_contact', 'Contact No.');
      const downloadLink = document.createElement('a');
      const blob = new Blob(['\ufeff', csv]);
      const url = URL.createObjectURL(blob);
      downloadLink.href = url;
      downloadLink.download = 'deliveryOrders.csv';
      downloadLink.click();
      downloadLink.remove();
    } catch (err) {
      dispatch(error(err.message));
    } finally {
      dispatch(stopLoading());
    }
  };
};

/**
 * Export multiple orders from firebase
 * @param {string} paging.orderBy order by field name
 * @param {string} paging.order get desc or asc
 * @param {string} filter filter text
 * @param {object} params collection fields
 */
export const exportCsvNew = (items) => {
  return async (dispatch) => {
    try {
      dispatch(startLoading());
      const lookupSku = await buildSkuLookup(items);
      const csvData = [];
      items.forEach((item) => {
        let shippingDetails = '';
        const {
          address,
          country,
          firstName,
          lastName,
          email,
          postalCode,
          contactNo,
          notes,
        } = item.shipping;

        shippingDetails = `${address}, ${country}`;

        item.products.forEach((product) => {
          const data = {
            csv_refNum: item.refNum,
            csv_status: item.status,
            csv_deliveryDate: moment
              .unix(item.deliveryDate.seconds)
              .format('DD-MMM-YYYY'),
            csv_deliveryTime: item.deliveryTime,
            csv_customer: `${firstName} ${lastName}`,
            csv_email: email,
            csv_shippingDetails: shippingDetails,
            csv_shippingPostal: postalCode,
            csv_contact: contactNo,
            csv_products: product.name,
            csv_variant: product.variant,
            csv_sku: lookupSku(product),
            csv_qty: product.qty,
            csv_productDesign: '',
            csv_productMaterial: '',
            csv_productColour: '',
            csv_notes: notes,
          };

          if (product.subVariants && product.subVariants.length > 0) {
            product.subVariants
              .filter((sub) => sub.value)
              .forEach((sub) => {
                if (sub.key === 'design') data.csv_productDesign = sub.value;
                if (sub.key === 'material')
                  data.csv_productMaterial = sub.value;
                if (sub.key === 'colour') data.csv_productColour = sub.value;
              });
          }
          csvData.push(data);
        });
      });

      const fields = Object.keys(csvData[0]);
      const opts = { fields };
      let csv = parse(csvData, opts);
      csv = csv
        .replace('csv_refNum', 'Ref No')
        .replace('csv_status', 'Status')
        .replace('csv_deliveryDate', 'Delivery Date')
        .replace('csv_deliveryTime', 'Delivery Time')
        .replace('csv_customer', 'Customer')
        .replace('csv_email', 'Email')
        .replace('csv_shippingDetails', 'Shipping Details')
        .replace('csv_shippingPostal', 'Postal')
        .replace('csv_contact', 'Contact No.')
        .replace('csv_products', 'Products')
        .replace('csv_variant', 'Variant')
        .replace('csv_sku', 'CMS SKU')
        .replace('csv_qty', 'Qty')
        .replace('csv_productDesign', 'Design')
        .replace('csv_productMaterial', 'Material')
        .replace('csv_productColour', 'Colour')
        .replace('csv_notes', 'Notes');
      const downloadLink = document.createElement('a');
      const blob = new Blob(['\ufeff', csv]);
      const url = URL.createObjectURL(blob);
      downloadLink.href = url;
      downloadLink.download = 'deliveryOrders.csv';
      downloadLink.click();
      downloadLink.remove();
    } catch (err) {
      dispatch(error(err.message));
    } finally {
      dispatch(stopLoading());
    }
  };
};
